import { useEffect, useState } from 'react'

/**
 * Demo / preview gates. Off by default for the public B2B build.
 * Flip on via URL param (?demo=1, ?paths=2, ?banner=1) and the flag
 * sticks for the rest of the session via sessionStorage so a
 * presenter can navigate around without re-adding the param.
 *
 * Add ?demo=0 (or any other 0/false) to clear an active flag.
 */
const SS_PREFIX = 'sd:flag:'

const PARAM_MAP = {
  demoPanel: { param: 'demo', truthy: ['1', 'true', 'on'] },
  path02: { param: 'paths', truthy: ['2', '02', 'all', 'both'] },
  detectBanner: { param: 'banner', truthy: ['1', 'true', 'on'] },
  // Reveals tools that aren't available yet, for partner demos only.
  toolsPreview: { param: 'tools', truthy: ['all', '1', 'true', 'on'] },
} as const

export type DemoFlagKey = keyof typeof PARAM_MAP

function readFlag(key: DemoFlagKey): boolean {
  if (typeof window === 'undefined') return false
  const cfg = PARAM_MAP[key]
  const params = new URLSearchParams(window.location.search)
  const raw = params.get(cfg.param)
  if (raw !== null) {
    const v = raw.toLowerCase()
    const next = cfg.truthy.includes(v as never)
    try {
      window.sessionStorage.setItem(SS_PREFIX + key, next ? '1' : '0')
    } catch {
      // ignore
    }
    return next
  }
  try {
    return window.sessionStorage.getItem(SS_PREFIX + key) === '1'
  } catch {
    return false
  }
}

export function useDemoFlag(key: DemoFlagKey): boolean {
  const [on, setOn] = useState<boolean>(() => readFlag(key))
  useEffect(() => {
    setOn(readFlag(key))
  }, [key])
  return on
}
